import React from "react";
import { Link } from "react-router-dom";
import {
  Mountain,
  Award,
  Users,
  Heart,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { useViewport } from "../../hooks/useViewport";

const About = () => {
  const { isMobile } = useViewport();

  const values = [
    {
      icon: <Heart size={36} />,
      title: "Warm Hospitality",
      description:
        "We treat every guest as family, ensuring a comfortable and memorable stay",
    },
    {
      icon: <Award size={36} />,
      title: "Quality Service",
      description: "Committed to excellence in every aspect of your experience",
    },
    {
      icon: <Mountain size={36} />,
      title: "Nature First",
      description:
        "Sustainable practices that respect and preserve our beautiful environment",
    },
    {
      icon: <Users size={36} />,
      title: "Community",
      description:
        "Supporting local communities and showcasing authentic Garhwali culture",
    },
  ];

  const stats = [
    { number: "2+", label: "Years Experience" },
    { number: "500+", label: "Happy Guests" },
    { number: "4.6", label: "Average Rating" },
    { number: "4", label: "Rooms Available" },
  ];

  const team = [
    {
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      name: "Rajesh Kumar",
      role: "Owner & Manager",
      desc: "Passionate about mountains and hospitality",
    },
    {
      image:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      name: "Priya Sharma",
      role: "Guest Relations",
      desc: "Ensuring every guest feels at home",
    },
    {
      image:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
      name: "Arun Singh",
      role: "Head Chef",
      desc: "Serving authentic Garhwali cuisine",
    },
  ];

  // Styles
  const sectionStyle = { padding: "80px 24px" };
  const containerStyle = { maxWidth: 1200, margin: "0 auto" };
  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: 20,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    overflow: "hidden",
    transition: "transform 0.3s, box-shadow 0.3s",
  };
  const btnPrimaryStyle = {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "14px 40px",
    borderRadius: 9999,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(22,163,74,0.4)",
    transition: "transform 0.2s",
  };
  const btnOutlineStyle = {
    backgroundColor: "transparent",
    color: "#16a34a",
    padding: "14px 40px",
    borderRadius: 9999,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    border: "2px solid #16a34a",
    cursor: "pointer",
    transition: "all 0.2s",
  };

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          height: 384,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=800&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(22,163,74,0.8) 0%, rgba(21,128,61,0.9) 100%)",
          }}
        ></div>
        <div
          style={{
            position: "relative",
            zIndex: 10,
            textAlign: "center",
            color: "#fff",
            padding: "0 24px",
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <p
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 6,
              fontWeight: "bolder",
              color: "rgba(255,255,255,0.7)",
              marginBottom: 12,
            }}
          >
            Our Story
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(40px, 6vw, 56px)",
              fontWeight: 900,
              marginBottom: 16,
            }}
          >
            About Us
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: 17,
              maxWidth: 600,
              margin: "0 auto",
            }}
          >
            Your Gateway to Himalayan Serenity in Dhanaulti
          </p>
        </div>
      </section>

      {/* Story */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 64,
              alignItems: "center",
            }}
          >
            <div
              style={{
                borderRadius: 32,
                overflow: "hidden",
                boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
              }}
            >
              <img
                src="/uploads/about-our-story-1780987712.jpg"
                alt="Mountain Resort"
                style={{ width: "100%", height: 500, objectFit: "cover" }}
              />
            </div>
            <div>
              {/* <p
                style={{
                  fontSize: 11,
                  color: "#16a34a",
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                Since 2014
              </p> */}
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 48,
                  fontWeight: 900,
                  color: "#1f2937",
                  lineHeight: 1.1,
                  marginBottom: 16,
                }}
              >
                Our Story
              </h2>
              <div
                style={{
                  width: 64,
                  height: 4,
                  background: "#16a34a",
                  borderRadius: 2,
                  marginBottom: 32,
                }}
              ></div>
              <div style={{ color: "#6b7280", fontSize: 17, lineHeight: 1.8 }}>
                <p style={{ marginBottom: 20 }}>
                  Nestled in the pristine hills of Dhanaulti at an altitude of
                  2,280 meters, Burans Hikes Home Stay was born from a passion
                  for the mountains and a desire to share the magic of the
                  Himalayas.
                </p>
                <p style={{ marginBottom: 20 }}>
                  We started as a small family-run homestay with 4 rooms. Today,
                  we've grown into a beloved retreat welcoming hundreds of
                  guests each year.
                </p>
                <p>
                  Our name "Burans" comes from the beautiful Rhododendron
                  flowers that bloom in vibrant red across our hillsides every
                  spring.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <p
              style={{
                fontSize: 11,
                color: "#16a34a",
                textTransform: "uppercase",
                letterSpacing: 4,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              What We Stand For
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                color: "#1f2937",
              }}
            >
              Our Core{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Values
              </span>
            </h2>
            <div
              style={{
                width: 64,
                height: 4,
                background: "#16a34a",
                borderRadius: 2,
                margin: "20px auto 0",
              }}
            ></div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 24,
            }}
          >
            {values.map(({ icon, title, description }) => (
              <div
                key={title}
                style={{
                  ...cardStyle,
                  padding: 32,
                  textAlign: "center",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    color: "#16a34a",
                    marginBottom: 20,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#1f2937",
                    marginBottom: 12,
                  }}
                >
                  {title}
                </h3>
                <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.6 }}>
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ padding: 80, backgroundColor: "#16a34a" }}>
        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 32,
              textAlign: "center",
            }}
          >
            {stats.map(({ number, label }) => (
              <div key={label}>
                <div
                  style={{
                    fontFamily: "inherit",
                    fontSize: 56,
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: 8,
                  }}
                >
                  {number}
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 2,
                    fontWeight: 500,
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {/* <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p
              style={{
                fontSize: 11,
                color: "#16a34a",
                textTransform: "uppercase",
                letterSpacing: 4,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              The People
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                color: "#1f2937",
              }}
            >
              Meet Our{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Team
              </span>
            </h2>
            <div
              style={{
                width: 64,
                height: 4,
                background: "#16a34a",
                borderRadius: 2,
                margin: "20px auto 16",
              }}
            ></div>
            <p style={{ color: "#6b7280", fontSize: 17 }}>
              Our dedicated team ensures your stay is comfortable and memorable
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 32,
              marginTop: 48,
            }}
          >
            {team.map(({ image, name, role, desc }) => (
              <div
                key={name}
                style={{
                  ...cardStyle,
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <figure style={{ height: 288, overflow: "hidden" }}>
                  <img
                    src={image}
                    alt={name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </figure>
                <div style={{ padding: 24, textAlign: "center" }}>
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#1f2937",
                      marginBottom: 4,
                    }}
                  >
                    {name}
                  </h3>
                  <p
                    style={{
                      color: "#16a34a",
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {role}
                  </p>
                  <p style={{ color: "#6b7280", fontSize: 14 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Location */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 64,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "#16a34a",
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                How to Find Us
              </p>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 48,
                  fontWeight: 900,
                  color: "#1f2937",
                  lineHeight: 1.1,
                  marginBottom: 16,
                }}
              >
                Find Us in
                <br />
                Dhanaulti
              </h2>
              <div
                style={{
                  width: 64,
                  height: 4,
                  background: "#16a34a",
                  borderRadius: 2,
                  marginBottom: 32,
                }}
              ></div>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: 17,
                  lineHeight: 1.8,
                  marginBottom: 32,
                }}
              >
                Located in the serene town of Dhanaulti in Uttarakhand, we're
                easily accessible from major cities while being far enough to
                offer true mountain peace.
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                {[
                  {
                    icon: <MapPin size={20} />,
                    title: "Address",
                    content: "Buranskhanda, Near Dhanaulti, Uttrakhand 248001",
                  },
                  {
                    icon: <Phone size={20} />,
                    title: "Phone",
                    content: "+91 8218579930\n+91 9068676184",
                  },
                  {
                    icon: <Mail size={20} />,
                    title: "Email",
                    content: "suryaprataps375@gmail.com",
                  },
                ].map(({ icon, title, content }) => (
                  <div
                    key={title}
                    style={{
                      display: "flex",
                      gap: 16,
                      padding: 20,
                      backgroundColor: "#fff",
                      borderRadius: 20,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div style={{ color: "#16a34a", flexShrink: 0 }}>
                      {icon}
                    </div>
                    <div>
                      <h4
                        style={{
                          fontWeight: 600,
                          color: "#1f2937",
                          marginBottom: 4,
                          fontSize: 14,
                        }}
                      >
                        {title}
                      </h4>
                      <p
                        style={{
                          color: "#6b7280",
                          fontSize: 13,
                          lineHeight: 1.5,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                borderRadius: 32,
                overflow: "hidden",
                minHeight: 384,
              }}
            >
              <iframe
                title="Burans Hikes location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3441.096816051585!2d78.23079937612283!3d30.404994901397053!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3908df832df12c43%3A0x5e23a2aba0682be3!2sBurans%20Hikes%20Home%20Stay%20(Resort)!5e0!3m2!1sen!2sin!4v1781382293190!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, display: "block", minHeight: 384 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={{ ...containerStyle, textAlign: "center", maxWidth: 700 }}>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 48,
              fontWeight: 900,
              color: "#1f2937",
              marginBottom: 16,
            }}
          >
            Ready to Experience
            <br />
            the Mountains?
          </h2>
          <p style={{ color: "#6b7280", fontSize: 17, marginBottom: 40 }}>
            Book your stay with us and create unforgettable memories
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 16,
              justifyContent: "center",
            }}
          >
            <Link to="/rooms" style={{ ...btnPrimaryStyle }}>
              View Our Rooms
            </Link>
            <Link to="/contact" style={{ ...btnOutlineStyle }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
